/**
 * The tenant register — the operator's home screen.
 *
 * Access control is `OpsShell`'s job (`../shell/OpsShell`'s own doc comment),
 * not this component's: `routes.ts` mounts this INSIDE the shell, so by the
 * time it renders, `useOpsEntitlement` has already answered `entitled` and
 * `useOpsTenants` below is reading the very query that answer came from —
 * one request, not two.
 *
 * Platform design §16, revised 2026-09-10: nothing purges a tenant any more and no cap
 * limits how many one account may create, so this list grows without bound.
 * The filter row below is what stands between an operator and the noise, and
 * it must never shrink the list silently — every view always shows its own
 * count, checked or not, so the inert count stays on screen even while its
 * rows are hidden.
 */

import { useRef, useState, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';

import { getLocale } from '../../i18n';
import { integer } from '../../i18n/format';
import { EmptyState, QueryStates } from '../../chrome/state';
import { DataTable } from '../../chrome/table';
import { IconHint } from '../../chrome/hint';
import { Page, PageHeader, PageSection } from '../../chrome/page';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { useOpsTenants } from '../api/queries';
import type { OpsTenantRow } from '../api/schemas';
import { opsTitles } from '../shell/titles';
import { CreateOrganisationDialog } from './CreateOrganisationDialog';
import { registerColumns } from './columns';
import { FilterBar, OpenRequestChip, matchesView, type RegisterView } from './FilterBar';
import { registerCatalogue, t } from './messages';
import { countSegments, segmentOf } from './segments';

/** Not among `registerColumns`'s contract (its own doc comment says why) --
 *  appended here, once. Its header carries no visible text (the marker
 *  itself already names itself when present), but an empty `<th>` fails
 *  WCAG's table-header-has-text check, so the same label reads `sr-only`. */
const OPEN_REQUEST_COLUMN: ColumnDef<OpsTenantRow, unknown> = {
  id: 'openRequest',
  header: () => <span className="sr-only">{t('openRequest.some')}</span>,
  cell: ({ row }) => <OpenRequestChip open={row.original.open_credit_request} />,
};

/** A second way into the same row the name cell's `Link` already opens --
 *  never an expand-in-place control, so its destination and the name
 *  column's must always agree. Its header still needs text for the same
 *  WCAG reason as `OPEN_REQUEST_COLUMN`'s. */
const CHEVRON_COLUMN: ColumnDef<OpsTenantRow, unknown> = {
  id: 'chevron',
  header: () => <span className="sr-only">{t('rowLink')}</span>,
  cell: ({ row }) => (
    <IconHint label={t('rowLink')}>
      <Link to="/tenants/$tenantId" params={{ tenantId: row.original.tenant_id }}>
        <ChevronRight aria-hidden size={16} className="text-muted-foreground" />
      </Link>
    </IconHint>
  ),
};

export function Register(): ReactNode {
  const query = useOpsTenants();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<RegisterView>('active');
  const [openRequestOnly, setOpenRequestOnly] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const locale = getLocale();

  // `DataTable`'s `getRowId` only feeds `useReactTable`'s internal row
  // identity (`src/chrome/table/DataTable.tsx`) and renders no DOM attribute
  // a caller could focus by id — there is no `data-row-id` to select. So the
  // table's own (otherwise `sr-only`) caption is made focusable and takes
  // the focus.
  function focusTableAfterCreate(): void {
    const caption = tableWrapperRef.current?.querySelector('caption');
    if (caption === null || caption === undefined) return;
    caption.setAttribute('tabindex', '-1');
    caption.focus();
  }

  return (
    <Page>
      <PageHeader title={opsTitles.t('register')} />
      <QueryStates
        query={query}
        empty={
          <EmptyState
            title={t('empty.title')}
            description={t('empty.body')}
            action={
              <CreateOrganisationDialog
                triggerLabel={t('empty.action')}
                onCreated={focusTableAfterCreate}
              />
            }
          />
        }
      >
        {(rows) => {
          const counts = countSegments(rows);
          const total = rows.length;
          const openRequestCount = rows.filter((row) => row.open_credit_request).length;

          const query_ = search.trim().toLocaleLowerCase(locale);
          const searched =
            query_ === ''
              ? rows
              : rows.filter((row) => row.name.toLocaleLowerCase(locale).includes(query_));
          const visibleRows = searched.filter(
            (row) =>
              matchesView(view, segmentOf(row)) && (!openRequestOnly || row.open_credit_request),
          );
          const noMatch = query_ !== '' && visibleRows.length === 0;
          const columns = [...registerColumns(), OPEN_REQUEST_COLUMN, CHEVRON_COLUMN];

          return (
            <PageSection
              title={t('section.title')}
              actions={
                <>
                  <Badge variant="secondary">
                    {registerCatalogue.plural('section.count.other', total, {
                      n: integer(visibleRows.length, locale),
                      total: integer(total, locale),
                    })}
                  </Badge>
                  <CreateOrganisationDialog
                    triggerLabel={t('create.open')}
                    onCreated={focusTableAfterCreate}
                  />
                </>
              }
            >
              <FilterBar
                search={search}
                onSearchChange={setSearch}
                view={view}
                onViewChange={setView}
                counts={counts}
                total={total}
                openRequestOnly={openRequestOnly}
                onOpenRequestOnlyChange={setOpenRequestOnly}
                openRequestCount={openRequestCount}
              />
              {/* `DataTable`'s shared `TableCell` (`src/chrome/table/DataTable.tsx`) uses the
                  8px vertical padding every other table in the app wants; the sm Pedidos chip
                  only reaches the ruled 46-48px row here with 12px, so the extra padding is
                  scoped to this table rather than raised for every caller. */}
              <div ref={tableWrapperRef} className="[&_td]:py-3">
                <DataTable<OpsTenantRow>
                  columns={columns}
                  data={visibleRows}
                  initialSorting={[{ id: 'created_at', desc: false }]}
                  caption={opsTitles.t('register')}
                  emptyState={
                    noMatch ? (
                      <EmptyState
                        title={t('noMatch.title')}
                        description={t('noMatch.body', { query: search.trim() })}
                        action={
                          <Button variant="outline" onClick={() => setView('all')}>
                            {t('noMatch.searchAll')}
                          </Button>
                        }
                      />
                    ) : (
                      <EmptyState
                        title={t('empty.title')}
                        description={t('empty.body')}
                        action={
                          <CreateOrganisationDialog
                            triggerLabel={t('empty.action')}
                            onCreated={focusTableAfterCreate}
                          />
                        }
                      />
                    )
                  }
                />
              </div>
            </PageSection>
          );
        }}
      </QueryStates>
    </Page>
  );
}
